"use client";

import {
  COMPANY_ACCESS_DENIED_EVENT,
  type CompanyAccessDeniedDetail,
} from "@/utils/errors/companyAccessDenied";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";

type CompanyAccessDeniedContextType = {
  companyAccessDenied: CompanyAccessDeniedDetail | null;
  reportCompanyAccessDenied: (detail: CompanyAccessDeniedDetail) => void;
  clearCompanyAccessDenied: () => void;
};

const CompanyAccessDeniedContext = createContext<
  CompanyAccessDeniedContextType | undefined
>(undefined);

export const CompanyAccessDeniedProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [companyAccessDenied, setCompanyAccessDenied] =
    useState<CompanyAccessDeniedDetail | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleCompanyAccessDenied = (event: Event) => {
      const customEvent = event as CustomEvent<CompanyAccessDeniedDetail>;
      setCompanyAccessDenied(customEvent.detail);
    };

    window.addEventListener(
      COMPANY_ACCESS_DENIED_EVENT,
      handleCompanyAccessDenied as EventListener,
    );

    return () => {
      window.removeEventListener(
        COMPANY_ACCESS_DENIED_EVENT,
        handleCompanyAccessDenied as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    if (pathname === "/signin") {
      setCompanyAccessDenied(null);
    }
  }, [pathname]);

  const value = useMemo(
    () => ({
      companyAccessDenied,
      reportCompanyAccessDenied: (detail: CompanyAccessDeniedDetail) => {
        setCompanyAccessDenied(detail);
      },
      clearCompanyAccessDenied: () => {
        setCompanyAccessDenied(null);
      },
    }),
    [companyAccessDenied],
  );

  return (
    <CompanyAccessDeniedContext.Provider value={value}>
      {children}
    </CompanyAccessDeniedContext.Provider>
  );
};

export const useCompanyAccessDenied = () => {
  const context = useContext(CompanyAccessDeniedContext);
  if (!context) {
    throw new Error(
      "useCompanyAccessDenied must be used within a CompanyAccessDeniedProvider",
    );
  }
  return context;
};
